// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { CloverOAuthCompletionResult } from '@pillar-portal/api-types/ipg/clover/heartbeat';
import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    describeCloverOAuthCompletion,
    parseCloverOAuthCompletion,
    publishCloverOAuthCompletion,
} from '@/lib/clover/oauth-completion';

/**
 * Where the Clover OAuth callback sends the browser (BUG-278). It tells the
 * Portal tab that started the connection, which refreshes its Clover card, and
 * closes this window. When the browser keeps the window open, it shows the
 * result in Portal words, never a bare server page.
 */
export default function CloverOAuthCompletePage() {
    const [searchParams] = useSearchParams();
    const completion = useMemo(() => parseCloverOAuthCompletion(searchParams), [searchParams]);

    useEffect(() => {
        if (completion === null) return;
        publishCloverOAuthCompletion(completion, {
            openChannel: (name) => new BroadcastChannel(name),
            opener: window.opener instanceof Window ? window.opener : null,
            origin: window.location.origin,
        });
        window.close();
    }, [completion]);

    const message =
        completion === null
            ? 'Anchorbase could not read the Clover connection result. Return to Anchorbase and check the Clover card.'
            : describeCloverOAuthCompletion(completion);
    return (
        <div className="mx-auto max-w-lg p-6">
            <Card>
                <CardHeader>
                    <CardTitle>
                        {completion?.result === CloverOAuthCompletionResult.CONNECTED
                            ? 'Clover is connected'
                            : 'Clover was not connected'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <Alert
                        variant={
                            completion?.result === CloverOAuthCompletionResult.CONNECTED
                                ? 'default'
                                : 'destructive'
                        }
                    >
                        <AlertDescription>{message}</AlertDescription>
                    </Alert>
                    <Link className="text-sm underline" to="/dashboard">
                        Return to Anchorbase
                    </Link>
                </CardContent>
            </Card>
        </div>
    );
}
